import { createBaseQuery } from '../helper/createBaseQuery'
import { tokenSlice } from '../slice/tokenSlice'
import { validateQueryStringParameters } from '../../helpers/api'

const InvoiceApi = tokenSlice.injectEndpoints({
  reducerPath: 'invoiceApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['Invoice'],
  endpoints(builder) {
    return {
      getOrderList: builder.query({
        query: (params) => ({
          url: `Invoice/GetOrderListApproved`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getCurrencyList: builder.query({
        query: (businessUnitCode) => ({
          url: `Currency/GetCurrencyCodesByBusinessUnitCode/${businessUnitCode}`,
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),

      getProblematicInvoiceReasonList: builder.query({
        query: () => ({
          url: `Invoice/GetProblematicInvoiceReasonList`,
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),

      getRegisterAll: builder.query({
        query: (params) => ({
          url: `Invoice/RegisterDraft`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getInvoiceHeld: builder.query({
        query: (params) => ({
          url: `Invoice/RegisterHeld`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getRegisterAllMain: builder.query({
        query: (params) => ({
          url: `Invoice/RegisterAll`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getRegisterWFA: builder.query({
        query: (params) => ({
          url: `Invoice/RegisterWFA`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getInvoiceApproval: builder.query({
        query: (id) => ({
          url: `Invoice/ApprovalInfo?invoiceRegisterId=${id}`,
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),

      postRegisterInvoice: builder.mutation({
        query: (data) => ({
          url: 'Invoice/Save',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Invoice'],
      }),

      postApproveInvoiceRegister: builder.mutation({
        query: (data) => ({
          url: 'Invoice/RegisterSendToApprove',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Invoice'],
      }),

      deleteRegisterInvoice: builder.mutation({
        query: (data) => ({
          url: `Invoice/Delete`,
          body: data,
          method: 'DELETE',
        }),
        invalidatesTags: ['Invoice'],
      }),

      postChangeInvoiceStatus: builder.mutation({
        query: (data) => ({
          url: 'Invoice/RegisterChangeStatus',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Invoice'],
      }),

      invoiceRegisterDetailsLoad: builder.mutation({
        query: (data) => {
          if (!data) {
            return
          }

          return {
            url: 'Invoice/InvoiceRegisterServiceDetailsLoad',
            method: 'POST',
            body: data,
          }
        },
        invalidatesTags: ['Invoice'],
      }),

      postSaveInvoiceMatching: builder.mutation({
        query: (data) => ({
          url: 'Invoice/SaveInvoiceMatchingForService',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Invoice'],
      }),

      postSaveInvoiceMatchingGRN: builder.mutation({
        query: (data) => ({
          url: 'Invoice/SaveInvoiceMatchingForGRN',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Invoice'],
      }),

      getMatchingGRN: builder.query({
        query: (params) => {
          return {
            url: `Invoice/MatchingMainGRN`,
            method: 'GET',
            params,
          }
        },
        providesTags: ['Invoice'],
      }),

      getMatchedView: builder.query({
        query: (params) => ({
          url: `Invoice/MatchingMainGRNList`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getMatchingService: builder.query({
        query: (params) => {
          return {
            url: `Invoice/MatchingMainService`,
            method: 'GET',
            params,
          }
        },
        providesTags: ['Invoice'],
      }),

      getInvoiceAttachments: builder.query({
        query: (sourceId) => ({
          url: `Attachment/GetInvoiceAttachments?sourceId=${sourceId}`,
          method: 'GET',
        }),
        providesTags: ['Attachment'],
      }),

      getMatchingMain: builder.query({
        query: (params) => ({
          url: `Invoice/GetMatchingMain`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getRegisterListByOrder: builder.query({
        query: (params) => ({
          url: `Invoice/RegisterListByOrder`,
          method: 'GET',
          params: validateQueryStringParameters(params),
        }),
        providesTags: ['Invoice'],
      }),

      getRegisterLoadGrn: builder.query({
        query: (id) => {
          return {
            url: `Invoice/RegisterLoadGRN`,
            method: 'GET',
            params: {
              orderMainId: id,
            },
          }
        },
        providesTags: ['Invoice'],
      }),

      postGetDetailsMatching: builder.mutation({
        query: (data) => {
          return {
            url: `Invoice/GetDetails`,
            method: 'POST',
            body: data,
          }
        },
        invalidatesTags: ['Invoice'],
      }),

      getAdvanceInvoicesList: builder.query({
        query: (orderMainId) => ({
          url: `Invoice/GetAdvanceInvoicesList/${orderMainId}`,
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),

      getInvoiceRegisterList: builder.query({
        query: (orderMainId) => ({
          url: `Invoice/InvoiceRegisterList/${orderMainId}`,
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),

      rejectReasonsForInvoice: builder.query({
        query: () => ({
          url: 'General/RejectReasonsForInvoice',
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),

      getInvoiceMatchData: builder.query({
        query: (params) => ({
          url: `Invoice/GetInvoiceMatchData/${params.invoiceMatchingId}/${params.businessUnitId}`,
          method: 'GET',
        }),
        providesTags: ['Invoice'],
      }),
    }
  },
})

export const {
  useGetInvoiceApprovalQuery,
  useGetInvoiceHeldQuery,
  useGetOrderListQuery,
  useGetCurrencyListQuery,
  useGetProblematicInvoiceReasonListQuery,
  useGetRegisterAllQuery,
  useGetRegisterWFAQuery,
  useGetMatchingGRNQuery,
  useGetMatchingServiceQuery,
  useGetInvoiceAttachmentsQuery,
  usePostRegisterInvoiceMutation,
  usePostApproveInvoiceRegisterMutation,
  usePostChangeInvoiceStatusMutation,
  useDeleteRegisterInvoiceMutation,
  useGetMatchingMainQuery,
  useGetRegisterListByOrderQuery,
  useGetRegisterLoadGrnQuery,
  useGetAdvanceInvoicesListQuery,
  useGetInvoiceRegisterListQuery,
  useInvoiceRegisterDetailsLoadMutation,
  usePostSaveInvoiceMatchingMutation,
  usePostSaveInvoiceMatchingGRNMutation,
  usePostGetDetailsMatchingMutation,
  useRejectReasonsForInvoiceQuery,
  useGetRegisterAllMainQuery,
  useGetMatchedViewQuery,
  useGetInvoiceMatchDataQuery,
} = InvoiceApi
export { InvoiceApi }
